import { GridRenderCellParams } from '@mui/x-data-grid';
import { EDiffAction, EProperty } from '../data/Constants';
import {
  IConfigurationValue,
  IConfigurationVariable,
  IDiffFeature,
  ISalesText,
  ISection,
} from '../../types';

/**
 * This method check whether current feature is Modified or not
 * @param {{[key:string]:IDiffFeature}} feature current feature
 * @param {GridRenderCellParams} params current row
 * @returns {string} returns true or false
 */
export function checkIsFeatureModified(
  feature: { [key: string]: IDiffFeature },
  params: GridRenderCellParams
): string {
  if (feature.from != null && feature.from.options == null && feature.from.properties == null) {
    if (
      feature.from.code === params.row.featureCode ||
      feature.from.code === params.row.featureFamilyCode
    ) {
      return EDiffAction.Modified;
    }
  }
  return '';
}

/**
 * This method check whether current feature is deleted  or not
 * @param {{[key:string]:IDiffFeature}} feature current feature
 * @param {GridRenderCellParams} params current row
 * @returns {string} returns the  action type
 */
export function checkIsFeatureRemoved(
  feature: { [key: string]: IDiffFeature },
  params: GridRenderCellParams
): string {
  if (feature.code === params.row.featureFamilyCode) {
    return EDiffAction.Deleted;
  }
  return '';
}

/**
 * This method check whether current feature is added or not
 * @param {{[key:string]:IDiffFeature}} feature current feature
 * @param {GridRenderCellParams} params current row
 * @returns {string} returns the  action type
 */
export function checkIsOptionAdded(
  feature: { [key: string]: IDiffFeature },
  params: GridRenderCellParams
): string {
  if (feature.from == null && feature?.to?.options != null) {
    if (
      feature.to.code === params.row.featureCode ||
      feature.to.code === params.row.featureFamilyCode
    ) {
      return EDiffAction.Modified;
    }
  }
  return '';
}

/**
 * This method check whether current feature is added or not
 * @param {{[key:string]:IDiffFeature}} feature current feature
 * @param {GridRenderCellParams} params current row
 * @returns {EDiffAction} returns the  action type
 */
export function checkIsOptionRemovedFromUpdated(
  feature: { [key: string]: IDiffFeature },
  params: GridRenderCellParams
): string {
  if (feature.to == null && feature?.from?.options != null) {
    if (
      feature.from.code === params.row.featureCode ||
      feature.from.code === params.row.featureFamilyCode
    ) {
      return EDiffAction.Modified;
    }
  }
  return '';
}

/**
 * This method check whether current feature is deleted  or not
 * @param {{[key:string]:IDiffFeature}} feature current feature
 * @param {GridRenderCellParams} params current row
 * @returns {string} returns the  action type
 */
export function checkIsOptionRemoved(
  feature: { [key: string]: IDiffFeature },
  params: GridRenderCellParams
): string {
  if (feature.code === params.row.featureCode) {
    return EDiffAction.Deleted;
  }
  return '';
}

/**
 * This method check whether current feature OPT property is modified or not
 * @param {{[key:string]:IDiffFeature}} feature current feature
 * @param {GridRenderCellParams} params current row
 * @returns {string} returns the  action type
 */
export function checkIsPropertiesModified(
  feature: { [key: string]: IDiffFeature },
  params: GridRenderCellParams
): string {
  if (feature.from != null && feature.to != null && feature.from.properties != null) {
    if (
      feature.from.code === params.row.featureCode ||
      feature.from.code === params.row.featureFamilyCode
    ) {
      const optProp = feature.from.properties.find((p) => p.code === EProperty.Optional);
      if (optProp?.code === EProperty.Optional) {
        return EDiffAction.Modified;
      }
    }
  }
  return '';
}

/**
 * This function returns is any variable is assigned or not in variables array
 * @param {IConfigurationVariable[]} variables
 * @returns {boolean} returns boolean value
 */
export function hasAssignedVariables(variables: IConfigurationVariable[]): boolean {
  return variables.some((variable: IConfigurationVariable) =>
    variable.values.some(
      (value: IConfigurationValue) =>
        value.assigned && (variable.valueType !== 'Number' || value.value > 0)
    )
  );
}

/**
 * This function check is any assigned section is there in the section array
 * @param {ISection [] } sections section array
 * @returns {boolean} returns the boolean value
 */
export function hasAssignedSection(sections: ISection[]): boolean {
  return sections.some(
    (section: ISection) =>
      hasAssignedVariables(section.variables) ||
      (section.sections && hasAssignedSection(section.sections))
  );
}

/**
 * This method returns the value from the price data using the variable id
 * @param {Map<string,Map<string,IPriceData>>} mapData  price Map data
 * @param {string } id the input key
 * @returns { IPriceData | ISalesText | null } the feature price or sales text value
 */

export function getValueById(
  mapData: Map<string, Map<string, ISalesText>>,
  id: string
): ISalesText | null {
  if (mapData.size > 0) {
    for (const [_outerKey, innerMap] of mapData) {
      if (innerMap.has(id)) {
        return innerMap?.get(id) ?? null;
      }
    }
  }
  return null;
}

/**
 * This function returns the formatted date to show in the data table
 * @param {string} dateValue input value
 * @returns {string} returns formatted date
 */
export function formatDateString(dateValue: string): string {
  const date = new Date(dateValue);
  const month = ('0' + (date.getMonth() + 1)).slice(-2);
  const day = ('0' + date.getDate()).slice(-2);
  const year = date.getFullYear();
  const hour = ('0' + date.getHours()).slice(-2);
  const min = ('0' + date.getMinutes()).slice(-2);
  const seg = ('0' + date.getSeconds()).slice(-2);
  return month + '-' + day + '-' + year + ' ' + hour + ':' + min + ':' + seg;
}
